import { env } from './env';

/**
 * Fetch wrapper com:
 * - accessToken mantido em memória (nunca em localStorage — TECH-SPEC §8.3)
 * - refresh automático em 401 via POST /auth/refresh
 * - cookie httpOnly (SameSite=Lax) carrega o refreshToken; credentials: 'include'
 */

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export interface ApiRequest extends Omit<RequestInit, 'body'> {
  body?: unknown;
  path: string;
  query?: Record<string, string | number | boolean | undefined | null>;
}

async function refreshAccessToken(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        accessToken = null;
        return null;
      }
      const data = (await res.json()) as { accessToken?: string };
      accessToken = data.accessToken ?? null;
      return accessToken;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function buildUrl(path: string, query?: ApiRequest['query']): string {
  const base = env.apiBaseUrl.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${normalizedPath}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function newRequestId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isOfflineError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    typeof err.payload === 'object' &&
    err.payload !== null &&
    'offline' in err.payload &&
    (err.payload as { offline?: unknown }).offline === true
  );
}

export async function apiRequest<T = unknown>({
  path,
  query,
  body,
  headers,
  method = 'GET',
  ...init
}: ApiRequest): Promise<T> {
  const url = buildUrl(path, query);

  if (
    typeof navigator !== 'undefined' &&
    navigator.onLine === false &&
    MUTATION_METHODS.has(method.toUpperCase())
  ) {
    throw new ApiError(0, 'Sem conexão, tente novamente', { offline: true });
  }

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isFormData && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (accessToken) finalHeaders.set('Authorization', `Bearer ${accessToken}`);
  if (!finalHeaders.has('X-Request-Id')) finalHeaders.set('X-Request-Id', newRequestId());

  const init1: RequestInit = {
    ...init,
    method,
    headers: finalHeaders,
    credentials: 'include',
    body:
      body === undefined
        ? undefined
        : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
  };

  let response = await fetch(url, init1);

  if (response.status === 401 && path !== '/auth/refresh' && path !== '/auth/login') {
    const newToken = await refreshAccessToken();
    if (newToken) {
      finalHeaders.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(url, { ...init1, headers: finalHeaders });
    }
  }

  if (!response.ok) {
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = await response.text();
    }
    const message =
      (payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message: unknown }).message)
        : response.statusText) || `HTTP ${response.status}`;
    throw new ApiError(response.status, message, payload);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export const api = {
  get: <T>(path: string, query?: ApiRequest['query']) => apiRequest<T>({ method: 'GET', path, query }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>({ method: 'POST', path, body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>({ method: 'PUT', path, body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>({ method: 'PATCH', path, body }),
  delete: <T>(path: string) => apiRequest<T>({ method: 'DELETE', path }),
};
